import { useEffect, useMemo, useState } from "react";
import { getDoc } from "firebase/firestore";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import { useUserEmail } from "../../../SignIn/UserEmailContext";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import { getIODetailsRef } from "../../../Adviser/Tabs/IODetails/ioDetailsService";
import {
  adviserIoDetailsDocumentId,
  uniqueIoRecipientIdsByEmail,
} from "../utils/ioRecipientMapping";
import { getFCIStatementsPage } from "../../FCIStatements/fciStatementsService";
import { formatStatementDate, statementDateParts } from "../../FCIStatements/statementDate";
import { getIncomePeriods } from "../../utils/incomePeriods";
import {
  getIncomePeriodStartMonthKey,
  monthKeyFromDate,
} from "../utils/incomePeriod";
import { orderIoRowsForExport } from "../utils/ioSummaryExport";
import withMonthlyPaymentsAccess from "../components/withMonthlyPaymentsAccess";
import Pagination from "../../../DesignSystem/Pagination/Pagination";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import IconButton from "../../../DesignSystem/Buttons/IconButton";
import { ReactComponent as DownloadIcon } from "../../../../assets/icons/download.svg";
import IOPaymentFlags from "./IOPaymentFlags";

const PAGE_SIZE = 100;
const PREFERRED_COLUMNS = [
  "Index",
  "PaymentId",
  "Recipient",
  "RecipientCrmId",
  "PaymentCreated",
  // "IncomePeriodId",
  // "IncomePeriodName",
  "PolicyAdviser",
  "ClientName",
  "PolicyAccountNumber",
  "PolicyProvider",
  "BusinessType",
  "CommissionType",
  "PaymentAmount",
  "GrossReceived",
  "NormalisedSplit",
  "Split",
  "IntroducerName",
  "Introducers",
];
const EXPORT_COLUMNS = [
  "PaymentId",
  "Recipient",
  "PaymentCreated",
  // "IncomePeriodId",
  // "IncomePeriodName",
  "PolicyAdviser",
  "ClientName",
  "PolicyAccountNumber",
  "PolicyProvider",
  "BusinessType",
  "CommissionType",
  "PaymentAmount",
  "GrossReceived",
  "NormalisedSplit",
  "IntroducerName",
];
const downloadName = () =>
  `fci-statements-${new Date().toISOString().slice(0, 10)}.xlsx`;
const formatPeriodDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-GB");
};
const formatPeriodStart = (item) => {
  const explicitStart =
    item.startDatetime || item.startDate || item.incomePeriodStartDatetime;
  if (explicitStart) return formatPeriodDate(explicitStart);
  const encodedStart = String(item.incomePeriodId || "").match(
    /^(\d{4})-(\d{2})-(\d{2})$/,
  );
  if (!encodedStart) return String(item.incomePeriodName || item.incomePeriodId || "");
  const [, year, day, month] = encodedStart;
  return `${day}/${month}/${year}`;
};
const money = (value) => Number(value || 0).toLocaleString("en-GB", {
  style: "currency",
  currency: "GBP",
});
const isNumericColumn = (column) => [
  "PaymentAmount",
  "GrossReceived",
  "UnnormalisedSplit",
  "NormalisedSplit",
  "Split",
].includes(column);
const introducerValues = (value) => (Array.isArray(value) ? value : [value])
  .flatMap((item) => String(item ?? "").split(","))
  .map((item) => item.trim())
  .filter(Boolean);
const formatIntroducers = (value) => introducerValues(value).join(",\n");
const IntroducerList = ({ value }) => {
  const items = introducerValues(value);
  return (
    <span className="fit-column">
      {items.map((item, index) => (
        <span key={`${item}-${index}`}>{item}{index < items.length - 1 ? "," : ""}</span>
      ))}
    </span>
  );
};
const formatColumnName = (column) =>
  (String(column) === "PaymentId"
    ? "Payment Entry"
    : String(column) === "PolicyAdviser"
      ? "Selling Adviser"
    : String(column) === "PolicyAccountNumber"
      ? "Policy Number"
      : String(column) === "IntroducerName" || String(column) === "Introducer"
        ? "Introducers"
        : String(column))
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
const StatementDate = ({ value }) => {
  const parts = statementDateParts(value);
  return (
    <span className="fit-column" style={{ whiteSpace: "nowrap", minWidth: "max-content" }}>
      <span>{parts.date}</span>
      {parts.time ? <span>{parts.time}</span> : null}
    </span>
  );
};
const displayValue = (row, column) => {
  if (column === "Recipient") {
    return [row.Recipient, row.RecipientType]
      .map((value) => String(value ?? "").trim())
      .filter(Boolean)
      .join(" - ");
  }
  if (column === "PaymentCreated") return formatStatementDate(row[column]);
  if (column === "PaymentAmount" || column === "GrossReceived") return money(row[column]);
  if (column === "IntroducerName" || column === "Introducers") {
    return formatIntroducers(row.IntroducerName ?? row.Introducers);
  }
  return (column === "UnnormalisedSplit" || column === "NormalisedSplit" || column === "Split") && row[column] !== null && row[column] !== undefined
    ? `${Number(row[column]).toLocaleString("en-GB", { maximumFractionDigits: 4 })}%`
    : String(row[column] ?? "");
};
const exportValue = (row, column) => {
  if (column === "Recipient") return displayValue(row, column);
  if (column === "PaymentCreated") return formatStatementDate(row[column]);
  if (column === "NormalisedSplit") return row.NormalisedSplit ?? row.Split;
  if (column === "IntroducerName") return formatIntroducers(row.IntroducerName ?? row.Introducers);
  return row[column];
};

const IOSummary = ({ selectedMonth, monthOptions, onMonthChange }) => {
  const { userEmail } = useUserEmail();
  const { advisers } = useAdviserList();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [adviserOptions, setAdviserOptions] = useState([]);
  const [selectedAdviserId, setSelectedAdviserId] = useState("");
  const [incomePeriods, setIncomePeriods] = useState([]);
  const [period, setPeriod] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getIncomePeriods()
      .then(setIncomePeriods)
      .catch((loadError) => setError(loadError.message));
  }, []);
  const periodOptions = useMemo(() => {
    const options = incomePeriods.map((item) => {
      const paymentMonth = getIncomePeriodStartMonthKey(item);
      return {
        id: String(item.incomePeriodId),
        name: `${formatPeriodStart(item)} - ${formatPeriodDate(item.endDatetime) || "not closed"}`,
        paymentMonth,
      };
    });
    if (incomePeriods[0]) {
      options.unshift({
        id: "current",
        name: `${formatPeriodDate(incomePeriods[0].endDatetime) || "Latest close"} - Current`,
        paymentMonth: monthKeyFromDate(incomePeriods[0].endDatetime),
      });
    }
    return options;
  }, [incomePeriods]);
  useEffect(() => {
    if (!periodOptions.length) return;
    const selectedPeriod = periodOptions.find((option) => option.id === period);
    const nextPeriod =
      selectedPeriod || periodOptions.find((option) => option.id === "current") || periodOptions[0];
    if (nextPeriod.id !== period) setPeriod(nextPeriod.id);
    if (nextPeriod.paymentMonth && nextPeriod.paymentMonth !== selectedMonth) {
      setPage(1);
      onMonthChange(nextPeriod.paymentMonth);
    }
  }, [onMonthChange, period, periodOptions, selectedMonth]);

  const selectPaymentMonth = (paymentMonth) => {
    const matchingPeriod = periodOptions.find(
      (option) => option.paymentMonth === paymentMonth,
    );
    setPage(1);
    onMonthChange(paymentMonth);
    setPeriod(matchingPeriod?.id || "");
  };

  const selectIncomePeriod = (periodId) => {
    if (!periodId) {
      const matchingPeriod = periodOptions.find(
        (option) => option.paymentMonth === selectedMonth,
      );
      setPeriod(matchingPeriod?.id || "");
      return;
    }
    const selectedPeriod = periodOptions.find((option) => option.id === periodId);
    setPage(1);
    setPeriod(periodId);
    if (selectedPeriod?.paymentMonth) onMonthChange(selectedPeriod.paymentMonth);
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!period) {
        setRows([]);
        setTotal(0);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const filters = {
          _period: period,
          _sort: "PaymentCreated",
          _direction: "desc",
          AdviserId: selectedAdviserId,
        };
        const result = await getFCIStatementsPage(
          page,
          PAGE_SIZE,
          userEmail,
          filters,
        );
        if (active) {
          setRows(result.rows || []);
          setTotal(Number(result.total || 0));
        }
      } catch (loadError) {
        if (active) setError(loadError.message || "Unable to load FCI statements.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    load();
    return () => { active = false; };
  }, [page, period, selectedAdviserId, userEmail]);

  useEffect(() => {
    let active = true;
    Promise.allSettled(advisers.map(async (adviser) => {
      const email = adviser.email || adviser.id;
      if (!email) return null;
      const snapshot = await getDoc(
        getIODetailsRef(adviserIoDetailsDocumentId(adviser)),
      );
      return { adviser, details: snapshot.exists() ? snapshot.data() : {} };
    }))
      .then((results) => {
        if (!active) return;
        const options = [];
        const items = results
          .filter((result) => result.status === "fulfilled" && result.value)
          .map((result) => result.value);
        const uniqueIds = uniqueIoRecipientIdsByEmail(
          items.map(({ adviser, details }) => ({
            email: String(adviser.email || adviser.id || "").trim().toLowerCase(),
            isAppointed: adviser.isAppointed,
            adviser,
            ioDetails: details,
          })),
        );
        items.forEach(({ adviser, details }) => {
          const adviserEmail = String(
            adviser.email || adviser.id || "",
          ).trim().toLowerCase();
          const adviserIds = uniqueIds[adviserEmail] || [];
          if (adviserIds.length) {
              options.push({
                id: adviserIds.join(","),
                name: `${adviser.name || adviser.email} (${adviserIds.join(", ")})`,
              });
          }
        });
        setAdviserOptions(options.sort((left, right) => left.name.localeCompare(right.name, "en-GB")));
      })
      .catch(() => {
        if (active) {
          setAdviserOptions([]);
        }
      });
    return () => { active = false; };
  }, [advisers]);

  const columns = useMemo(() => {
    const available = new Set(Object.keys(rows[0] || {}));
    const introducerColumn = available.has("IntroducerName")
      ? "IntroducerName"
      : available.has("Introducers")
        ? "Introducers"
        : "";
    return [
      "Index",
      ...PREFERRED_COLUMNS.filter(
        (column) =>
          column !== "Index" &&
          column !== "IntroducerName" &&
          column !== "Introducers" &&
          available.has(column),
      ),
      ...(introducerColumn ? [introducerColumn] : []),
    ];
  }, [rows]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const exportExcel = async () => {
    setExporting(true);
    setError("");
    try {
      const result = await getFCIStatementsPage(1, 100000, userEmail, {
        _period: period,
        _sort: "PaymentCreated",
        _direction: "asc",
        _export: "true",
        AdviserId: selectedAdviserId,
      });
      const orderedRows = orderIoRowsForExport(result.rows || []);
      const exportRows = orderedRows.map((row) => {
        const copy = {};
        EXPORT_COLUMNS.forEach((column) => {
          const heading = column === "PaymentId"
            ? "Payment Entry"
            : column === "PolicyAdviser"
              ? "Selling Adviser"
            : column === "ClientName"
              ? "Client Name"
            : column === "PolicyAccountNumber"
              ? "Policy Number"
              : column === "NormalisedSplit"
                ? "Split"
                : column === "IntroducerName"
                  ? "Introducers"
                  : column;
          copy[heading] = exportValue(row, column);
        });
        return copy;
      });
      const sheet = XLSX.utils.json_to_sheet(exportRows);
      const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
      sheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
      sheet["!cols"] = Object.keys(exportRows[0] || {}).map(() => ({ wch: 18 }));
      for (let column = range.s.c; column <= range.e.c; column += 1) {
        const header = sheet[XLSX.utils.encode_cell({ r: 0, c: column })];
        if (header) {
          header.s = {
            fill: { fgColor: { rgb: "081E56" } },
            font: { color: { rgb: "FFFFFF" }, bold: true },
          };
        }
        for (let row = 1; row <= range.e.r; row += 1) {
          const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
          if (cell) {
            cell.s = {
              fill: { fgColor: { rgb: row % 2 ? "EAF4FB" : "FFFFFF" } },
              border: { bottom: { style: "thin", color: { rgb: "D6E0EA" } } },
            };
          }
        }
      }
      const exportColumns = Object.keys(exportRows[0] || {});
      const introducersColumn = exportColumns.indexOf("Introducers");
      if (introducersColumn >= 0) {
        orderedRows.forEach((_, index) => {
          const cell = sheet[XLSX.utils.encode_cell({ r: index + 1, c: introducersColumn })];
          if (cell) cell.s = {
            ...cell.s,
            alignment: { vertical: "top", wrapText: true },
          };
        });
      }
      [
        ["Client Name", "ClientUrl", "Open client in Intelliflo"],
        ["Policy Number", "PlanUrl", "Open plan in Intelliflo"],
      ].forEach(([heading, urlKey, tooltip]) => {
        const column = exportColumns.indexOf(heading);
        if (column < 0) return;
        orderedRows.forEach((row, index) => {
          if (!row[urlKey]) return;
          const cell = sheet[XLSX.utils.encode_cell({ r: index + 1, c: column })];
          if (!cell) return;
          cell.l = { Target: row[urlKey], Tooltip: tooltip };
          cell.s = {
            ...cell.s,
            font: { color: { rgb: "0563C1" }, underline: true },
          };
        });
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "FCI Statements");
      saveAs(
        new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })]),
        downloadName(),
      );
    } catch (exportError) {
      setError(exportError.message || "Unable to export FCI statements.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fit-column gap-l">
      <div className="padding-s" role="group" aria-label="IO Summary payment month">
        <RadioGroup
          name="io-summary-month"
          options={monthOptions}
          value={selectedMonth}
          onChange={(event) => selectPaymentMonth(event.target.value)}
          inline
        />
      </div>
      <IOPaymentFlags
        selectedMonth={selectedMonth}
        selectedAdviserId={selectedAdviserId}
      />
      <section aria-label="IO statements" className="card-corner-radius io-summary-table fit-column gap-s">
        <div className="row space-between align-end gap-m padding-s">
          <div className="row align-end gap-s">
            <div className="io-adviser-filter">
              <CustomSelect
                label="Adviser"
                options={adviserOptions}
                value={selectedAdviserId}
                placeholder="All advisers"
                onChange={(event) => {
                  setPage(1);
                  setSelectedAdviserId(event.target.value);
                }}
              />
            </div>
            <div className="io-period-filter">
              <CustomSelect
                label="Income period"
                options={periodOptions}
                value={period}
                overlay={loading}
                onChange={(event) => selectIncomePeriod(event.target.value)}
              />
            </div>
            <IconButton
              icon={<DownloadIcon aria-hidden="true" />}
              onClick={exportExcel}
              disabled={exporting || !period}
              loading={exporting}
              label="Download Excel"
              loadingLabel="Downloading Excel..."
            />
          </div>
          {totalPages > 1 ? (
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          ) : null}
        </div>
        <div className="table-scroll-container">
          <table>
          <thead><tr>{columns.map((column) => (
            <th key={column} className={`FP_Help_Text_Semibold uppercase white-text ${isNumericColumn(column) ? "text-align-right" : ""}`}>
              {column === "MatchedAdviser"
                ? "Adviser"
                : column === "RecipientCrmId"
                  ? "Recipient CRM ID"
                  : column === "NormalisedSplit"
                    ? "Split"
                    : formatColumnName(column)}
            </th>
          ))}</tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={Math.max(columns.length, 1)} className="FP_Help_Text primary">Loading live FCI data...</td></tr>
            ) : error ? (
              <tr><td colSpan={Math.max(columns.length, 1)} className="FP_Help_Text text-error">{error}</td></tr>
            ) : rows.length ? rows.map((row, index) => {
              return (
                <tr key={`${row.CashMatchId || row.PaymentId || "row"}-${index}`}>
                  {columns.map((column) => (
                    <td key={column} className={`FP_Help_Text text-black ${isNumericColumn(column) ? "text-align-right" : ""}`}>
                      {column === "Index"
                        ? (page - 1) * PAGE_SIZE + index + 1
                        : column === "PaymentCreated"
                          ? <StatementDate value={row[column]} />
                        : column === "ClientName" && row.ClientUrl
                        ? <a className="table-url-link text-primary-1" href={row.ClientUrl} target="_blank" rel="noreferrer">{displayValue(row, column)}</a>
                        : column === "PolicyAccountNumber" && row.PlanUrl
                          ? <a className="table-url-link text-primary-1" href={row.PlanUrl} target="_blank" rel="noreferrer">{displayValue(row, column)}</a>
                          : column === "IntroducerName" || column === "Introducers"
                            ? <IntroducerList value={row.IntroducerName ?? row.Introducers} />
                          : displayValue(row, column)}
                    </td>
                  ))}
                </tr>
              );
            }) : (
              <tr><td colSpan={Math.max(columns.length, 1)} className="FP_Help_Text primary">No FCI entries found for the current period.</td></tr>
            )}
          </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default withMonthlyPaymentsAccess(IOSummary);
